import type {
  AgentTraceContributor,
  AgentTraceRange,
  AgentTraceRecord,
} from "@afar-research/contracts";
export type {
  AgentTraceContributor,
  AgentTraceRange,
  AgentTraceConversation,
  AgentTraceRecord,
} from "@afar-research/contracts";
/**
 * Reading git-ai Authorship Logs (Git AI Standard v3, the notes under
 * `refs/notes/ai`) and converting them to Agent Trace records.
 *
 * The Authorship Log is the only record of who wrote what; everything here is
 * derived from it on read, so a Contributions view and an Agent Trace Export
 * can never disagree with the notes they came from.
 */

export interface AuthorshipLineRange {
  readonly start: number;
  readonly end: number;
}

export interface AuthorshipAttestation {
  /** `s_<session>::t_<trace>`, `h_<human>`, or a legacy prompt hash. */
  readonly key: string;
  readonly ranges: ReadonlyArray<AuthorshipLineRange>;
}

export interface AuthorshipFile {
  readonly path: string;
  readonly attestations: ReadonlyArray<AuthorshipAttestation>;
}

export interface AuthorshipAgent {
  readonly tool: string;
  /** The tool's conversation id; AFAR reports the thread id. */
  readonly id: string;
  readonly model: string;
}

export interface AuthorshipLog {
  readonly files: ReadonlyArray<AuthorshipFile>;
  readonly sessions: Readonly<Record<string, AuthorshipAgent>>;
  readonly prompts: Readonly<Record<string, AuthorshipAgent>>;
  readonly humans: Readonly<Record<string, { readonly author: string }>>;
}

export interface AuthorshipModelLines {
  readonly tool: string;
  readonly model: string;
  readonly aiLines: number;
}

/** Lines added by one or more commits, by who wrote them. */
export interface AuthorshipSummary {
  readonly aiLines: number;
  readonly humanLines: number;
  /** Added lines no Authorship Log attests: credited to nobody. */
  readonly unknownLines: number;
  readonly byModel: ReadonlyArray<AuthorshipModelLines>;
}

/**
 * The Agent Providers whose work AFAR records, under the tool names git-ai's
 * own tooling uses for them, so `git ai blame` and `git ai stats` recognise
 * AFAR's sessions.
 */
const AUTHORSHIP_TOOLS = [
  { tool: "codex", driver: "codex", modelProvider: "openai", label: "Codex" },
  { tool: "claude", driver: "claudeAgent", modelProvider: "anthropic", label: "Claude Code" },
] as const;

const authorshipTool = (tool: string) => AUTHORSHIP_TOOLS.find((entry) => entry.tool === tool);

/** git-ai's tool name for an Agent Provider's driver. */
export const authorshipToolForDriver = (driver: string): string =>
  AUTHORSHIP_TOOLS.find((entry) => entry.driver === driver)?.tool ?? driver;

/** How the app names the tool an Authorship Log credits; another tool keeps its own name. */
export const authorshipToolLabel = (tool: string): string => authorshipTool(tool)?.label ?? tool;

export const EMPTY_AUTHORSHIP_SUMMARY: AuthorshipSummary = {
  aiLines: 0,
  humanLines: 0,
  unknownLines: 0,
  byModel: [],
};

const SUPPORTED_SCHEMA_MAJOR = "authorship/3.";
const DIVIDER = "---";

type Contributor =
  | { readonly type: "ai"; readonly agent: AuthorshipAgent; readonly sessionKey: string }
  | { readonly type: "human"; readonly humanKey: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function readAgents(value: unknown): Record<string, AuthorshipAgent> {
  if (!isRecord(value)) return {};
  const agents: Record<string, AuthorshipAgent> = {};
  for (const [key, entry] of Object.entries(value)) {
    const agentId = isRecord(entry) ? entry.agent_id : undefined;
    if (!isRecord(agentId)) continue;
    const { tool, id, model } = agentId;
    if (typeof tool !== "string" || typeof id !== "string" || typeof model !== "string") continue;
    agents[key] = { tool, id, model };
  }
  return agents;
}

function readHumans(value: unknown): Record<string, { readonly author: string }> {
  if (!isRecord(value)) return {};
  const humans: Record<string, { readonly author: string }> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (isRecord(entry) && typeof entry.author === "string") humans[key] = { author: entry.author };
  }
  return humans;
}

function parseRanges(text: string): ReadonlyArray<AuthorshipLineRange> | null {
  const ranges: AuthorshipLineRange[] = [];
  for (const part of text.split(",")) {
    const match = /^(\d+)(?:-(\d+))?$/.exec(part);
    if (match === null) return null;
    const start = Number(match[1]);
    const end = match[2] === undefined ? start : Number(match[2]);
    if (start < 1 || end < start) return null;
    ranges.push({ start, end });
  }
  return ranges;
}

function parseAttestations(section: string): ReadonlyArray<AuthorshipFile> | null {
  const files: { path: string; attestations: AuthorshipAttestation[] }[] = [];
  for (const line of section.split("\n")) {
    if (line.trim() === "") continue;
    if (line.startsWith("  ")) {
      const current = files.at(-1);
      const [key, ranges, ...rest] = line.trim().split(" ");
      const parsed = ranges === undefined ? null : parseRanges(ranges);
      if (current === undefined || key === undefined || parsed === null || rest.length > 0) {
        return null;
      }
      current.attestations.push({ key, ranges: parsed });
      continue;
    }
    const path =
      line.startsWith('"') && line.endsWith('"') && line.length >= 2 ? line.slice(1, -1) : line;
    files.push({ path, attestations: [] });
  }
  return files;
}

/** Returns null for anything that is not a v3 Authorship Log. */
export function parseAuthorshipLog(note: string): AuthorshipLog | null {
  const lines = note.replace(/\r\n/g, "\n").split("\n");
  const dividerIndex = lines.indexOf(DIVIDER);
  if (dividerIndex === -1) return null;
  const files = parseAttestations(lines.slice(0, dividerIndex).join("\n"));
  if (files === null) return null;
  let metadata: unknown;
  try {
    metadata = JSON.parse(lines.slice(dividerIndex + 1).join("\n"));
  } catch {
    return null;
  }
  if (
    !isRecord(metadata) ||
    typeof metadata.schema_version !== "string" ||
    !metadata.schema_version.startsWith(SUPPORTED_SCHEMA_MAJOR)
  ) {
    return null;
  }
  return {
    files,
    sessions: readAgents(metadata.sessions),
    prompts: readAgents(metadata.prompts),
    humans: readHumans(metadata.humans),
  };
}

function contributorOf(log: AuthorshipLog, key: string): Contributor | null {
  if (key.startsWith("h_")) return key in log.humans ? { type: "human", humanKey: key } : null;
  if (key.startsWith("s_")) {
    const sessionKey = key.split("::")[0] ?? key;
    const agent = log.sessions[sessionKey];
    return agent === undefined ? null : { type: "ai", agent, sessionKey };
  }
  const agent = log.prompts[key];
  return agent === undefined ? null : { type: "ai", agent, sessionKey: key };
}

const lineCount = (ranges: ReadonlyArray<AuthorshipLineRange>) =>
  ranges.reduce((total, range) => total + range.end - range.start + 1, 0);

const modelKey = (tool: string, model: string) => `${tool}\u0000${model}`;

function sortedModels(byKey: Map<string, AuthorshipModelLines>): AuthorshipModelLines[] {
  return [...byKey.values()].toSorted(
    (left, right) => left.tool.localeCompare(right.tool) || left.model.localeCompare(right.model),
  );
}

/**
 * One commit's lines by author. `addedLines` is git's count of lines the commit
 * added; whatever the Authorship Log does not attest is untracked.
 */
export function summarizeAuthorshipLog(
  log: AuthorshipLog | null,
  addedLines: number,
): AuthorshipSummary {
  let aiLines = 0;
  let humanLines = 0;
  const byModel = new Map<string, AuthorshipModelLines>();
  for (const file of log?.files ?? []) {
    for (const attestation of file.attestations) {
      const contributor = log === null ? null : contributorOf(log, attestation.key);
      if (contributor === null) continue;
      const lines = lineCount(attestation.ranges);
      if (contributor.type === "human") {
        humanLines += lines;
        continue;
      }
      aiLines += lines;
      const { tool, model } = contributor.agent;
      const key = modelKey(tool, model);
      byModel.set(key, { tool, model, aiLines: (byModel.get(key)?.aiLines ?? 0) + lines });
    }
  }
  return {
    aiLines,
    humanLines,
    unknownLines: Math.max(0, addedLines - aiLines - humanLines),
    byModel: sortedModels(byModel),
  };
}

export function addAuthorshipSummaries(
  left: AuthorshipSummary,
  right: AuthorshipSummary,
): AuthorshipSummary {
  const byModel = new Map<string, AuthorshipModelLines>();
  for (const entry of [...left.byModel, ...right.byModel]) {
    const key = modelKey(entry.tool, entry.model);
    byModel.set(key, { ...entry, aiLines: (byModel.get(key)?.aiLines ?? 0) + entry.aiLines });
  }
  return {
    aiLines: left.aiLines + right.aiLines,
    humanLines: left.humanLines + right.humanLines,
    unknownLines: left.unknownLines + right.unknownLines,
    byModel: sortedModels(byModel),
  };
}

// Agent Trace (https://agent-trace.dev), record schema version 0.1.

export const AGENT_TRACE_VERSION = "0.1";
const AGENT_TRACE_METADATA_KEY = "dev.afar-research";
const MODEL_ID_MAX_LENGTH = 250;

/** A models.dev-style `provider/model` id. */
function agentTraceModelId(agent: AuthorshipAgent): string {
  const provider = authorshipTool(agent.tool)?.modelProvider ?? agent.tool;
  const id = agent.model.includes("/") ? agent.model : `${provider}/${agent.model}`;
  return id.slice(0, MODEL_ID_MAX_LENGTH);
}

/**
 * A stable UUID-shaped id derived from the commit, so exporting the same
 * commit twice yields the same record id.
 */
function recordIdForCommit(commitSha: string): string {
  const hex = commitSha
    .toLowerCase()
    .replace(/[^0-9a-f]/g, "")
    .padEnd(32, "0");
  const variant = "89ab"[Number.parseInt(hex[16] ?? "0", 16) % 4];
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `5${hex.slice(13, 16)}`,
    `${variant}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join("-");
}

/**
 * One commit's Authorship Log as an Agent Trace record. Each AI session and
 * each human becomes one conversation per file; untracked lines are left out,
 * because Agent Trace reads a missing range as no claim.
 */
export function authorshipLogToAgentTraceRecord(input: {
  readonly log: AuthorshipLog;
  readonly commitSha: string;
  readonly committedAt: string;
  readonly toolVersion: string;
}): AgentTraceRecord {
  const { log } = input;
  const agentsInOrder = new Map<string, AuthorshipAgent>();
  const files = log.files.flatMap((file) => {
    const conversations = new Map<
      string,
      { contributor: AgentTraceContributor; ranges: AgentTraceRange[] }
    >();
    for (const attestation of file.attestations) {
      const contributor = contributorOf(log, attestation.key);
      if (contributor === null) continue;
      const conversationKey =
        contributor.type === "ai" ? contributor.sessionKey : contributor.humanKey;
      if (contributor.type === "ai") agentsInOrder.set(contributor.sessionKey, contributor.agent);
      const conversation = conversations.get(conversationKey) ?? {
        contributor:
          contributor.type === "ai"
            ? { type: "ai" as const, model_id: agentTraceModelId(contributor.agent) }
            : { type: "human" as const },
        ranges: [],
      };
      conversation.ranges.push(
        ...attestation.ranges.map((range) => ({ start_line: range.start, end_line: range.end })),
      );
      conversations.set(conversationKey, conversation);
    }
    return conversations.size === 0
      ? []
      : [
          {
            path: file.path,
            conversations: [...conversations.values()].map((conversation) => ({
              ...conversation,
              ranges: conversation.ranges.toSorted(
                (left, right) => left.start_line - right.start_line,
              ),
            })),
          },
        ];
  });
  return {
    version: AGENT_TRACE_VERSION,
    id: recordIdForCommit(input.commitSha),
    timestamp: input.committedAt,
    vcs: { type: "git", revision: input.commitSha },
    tool: { name: "afar-research", version: input.toolVersion },
    files,
    metadata: {
      [AGENT_TRACE_METADATA_KEY]: {
        conversations: [...agentsInOrder.values()].map((agent) => ({
          tool: agent.tool,
          model: agent.model,
          conversation_id: agent.id,
        })),
      },
    },
  };
}
